import { useEffect, useRef, useState } from "react";
import Reveal from "@Components/Reveal/Reveal";
/**
 * Supplied icons — 24-grid monochrome line drawings, `stroke="black"` baked in.
 *
 * These replaced the live site's coloured insight illustrations (the pink heart-snap set, pulled
 * from amber-user-website's CDN). Two things changed with them, and both are deliberate:
 *
 *   · SIZE. Those were drawn on a 46 grid and rendered at 46. These are drawn on 24, so rendering
 *     them at 46 would scale a 1.5px stroke to nearly 3px — heavier than the artwork intends. 32px
 *     is close enough to their own grid to keep the weight honest.
 *   · WEIGHT. Line icons in ink rather than filled illustrations in pink, which is also more at
 *     home on this page: it is monochrome everywhere else, and the coloured set was the only
 *     colour on it.
 *
 * The file names describe what they draw, unlike the CDN set — whose names were rotated one place
 * against their contents. No mapping trap here.
 */
import InlineLottie from "@Components/InlineLottie/InlineLottie";
import styles from "./WhyBook.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { pageAsset } from "@Utils/getStaticAssetPath";

// Images and animations come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("HowItWorksV2");

// ── THE MARKS ──────────────────────────────────────────────────────────────
// Animated now, not flat SVGs. Same four drawings at the same line weight — the
// Iconly set these came from is the one the other v2 pages already animate with,
// so this section stops being the only still one on the page.
const lottieBookings = asset("why-book-flash.json");
const lottiePrice = asset("why-book-discount-down.json");
const lottieAssistance = asset("why-book-phone-call-help.json");
const lottieVerified = asset("why-book-document-shield.json");

/**
 * "Why book with amber" — the reassurance section, between the three steps and the footer.
 *
 * ── The layout ──────────────────────────────────────────────────────────────
 * A centred two-line heading in the page's own pattern — statement, then the second line
 * dropped back to grey, exactly as `Hero` and `Steps` do it — and under it four columns
 * divided by full-height hairlines. Each column reads top to bottom: a small icon, the
 * claim, its copy. Nothing else.
 *
 * The icons are PLACEHOLDERS pending the real set — see the note on `Reason.icon` for how to
 * swap them in.
 *
 * No supporting paragraph and no "Learn more" links. The heading's grey second line is the
 * support and the columns carry their own copy; a link under each one would need a page
 * behind it, and these four are promises rather than topics.
 *
 * This is a deliberate step BACK from a version that carried plate numbers (`FIG 01`…) and
 * large generated isometric wireframes, one per column. That version was a faithful copy of
 * a different reference and it was too much furniture for what this section says: four short
 * promises. The drawings needed a 210px stage each, which set the section's height, and the
 * plate numbers implied a numbered sequence the four claims do not have.
 *
 * ── The copy ────────────────────────────────────────────────────────────────
 * These four are amber's own live promises, in amber's own words and order. One edit: each
 * line ends in a single full stop, where the source renders a double ("..book with us.."),
 * which reads as a typo rather than as an ellipsis at this size. Nothing else is changed —
 * these are commitments the business makes, so the wording is not ours to improve.
 */

interface Reason {
  /** Translation key for the claim. */
  title: string;
  /** Translation key for the copy under it. */
  body: string;
  /**
   * The column's icon, as an imported asset URL.
   *
   * All four are the live site's icons now. The placeholder path stays, because it is what makes
   * an unset icon obvious rather than invisible — see the note on `Icon`.
   */
  lottie?: string;
}

const REASONS: Reason[] = [
  {
    title: "howitworks.v2.whybook.reason1.title",
    lottie: lottieBookings,
    body: "howitworks.v2.whybook.reason1.body",
  },
  {
    title: "howitworks.v2.whybook.reason2.title",
    lottie: lottiePrice,
    body: "howitworks.v2.whybook.reason2.body",
  },
  {
    title: "howitworks.v2.whybook.reason3.title",
    lottie: lottieAssistance,
    body: "howitworks.v2.whybook.reason3.body",
  },
  {
    title: "howitworks.v2.whybook.reason4.title",
    lottie: lottieVerified,
    body: "howitworks.v2.whybook.reason4.body",
  },
];

/**
 * The column's icon, or a placeholder while there isn't one.
 *
 * Both render into the same 40px box, so the section's layout is already final — the real
 * icons will drop in without moving a single line of copy.
 *
 * The placeholder is a dashed empty square on purpose. It has to be unmistakably temporary:
 * anything that reads as a finished mark is the kind of placeholder that survives into
 * production because nobody notices it is still there.
 */
const Icon = ({ data, play, onHover }: { data?: string; play?: boolean; onHover?: () => void }) =>
  data ? (
    <span className={styles.icon} onMouseEnter={onHover}>
      <InlineLottie src={data} size={40} play={play} />
    </span>
  ) : (
    <span className={styles.iconPlaceholder} aria-hidden="true" />
  );

/** Beat between one mark starting and the next, so they arrive left to right. */
const STAGGER = 180;

const WhyBook = () => {
  const { t } = useTranslationHook();
  /**
   * The mark that should be replaying: one the stagger has reached, or a hovered
   * column.
   *
   * InlineLottie parks on its finished frame and replays on hover, which is fine
   * with a mouse and nothing at all on a phone — there IS no hover there, so the
   * marks would sit still for exactly the readers who see this section stacked.
   * The one-shot below covers that: it fires once, when the grid first reaches
   * the viewport, and hover takes over afterwards. Same arrangement as
   * ScholarshipV2's Categories.
   */
  const [activeItem, setActiveItem] = useState<number | null>(null);
  const gridRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const node = gridRef.current;
    if (!node || typeof IntersectionObserver !== "function") return undefined;

    const timers: number[] = [];
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        REASONS.forEach((_, i) => {
          timers.push(
            window.setTimeout(() => setActiveItem(i), i * STAGGER),
            // Released again so a later hover is a fresh change of `play` rather
            // than a no-op, and so no column stays latched as "playing".
            window.setTimeout(() => setActiveItem(null), i * STAGGER + STAGGER - 10),
          );
        });
      },
      { threshold: 0.35 },
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      timers.forEach(window.clearTimeout);
    };
  }, []);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <Reveal className={styles.headingBlock}>
          <h2 className={styles.heading}>
            {t("howitworks.v2.whybook.heading")}
            <br />
            <span className={styles.headingMuted}>{t("howitworks.v2.whybook.headingMuted")}</span>
          </h2>
        </Reveal>

        <ul className={styles.grid} ref={gridRef}>
          {REASONS.map((reason, i) => (
            // Staggered off the index so the columns arrive left to right rather than all at
            // once, matching how the steps above come in one at a time.
            <Reveal as="li" key={reason.title} className={styles.item} delay={i * 90}>
              {/* The hover lives on the mark, not the <li>: `Reveal` takes a fixed
                prop list and forwards nothing, so an onMouseEnter passed to it
                would be silently dropped. */}
              <Icon data={reason.lottie} play={activeItem === i} onHover={() => setActiveItem(i)} />
              <h3 className={styles.itemTitle}>{t(reason.title)}</h3>
              <p className={styles.itemText}>{t(reason.body)}</p>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default wrapperHOC(WhyBook, {
  componentName: "WhyBook-HowItWorksV2",
  showForChina: true,
});
